import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowUp, FileText, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import BrandStudioShell from "@/components/brandStudio/BrandStudioShell";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import PrivateReferenceImage from "@/components/masterOrder/PrivateReferenceImage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MAX_BRAND_REFERENCES, referenceCountLabel } from "@/lib/brandReferences.mjs";
import {
  createBrandReference,
  deleteBrandReference,
  getBrandProfileV2,
  listBrandReferences,
  updateBrandReference,
  uploadUserMediaFile,
} from "@/lib/brandStudioApi";

const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function errorText(error) {
  return error?.response?.data?.detail || "Не удалось выполнить действие. Попробуй ещё раз.";
}

function ReferenceItem({ reference, onOpen, onImageReady }) {
  return (
    <article className="min-w-0 overflow-hidden rounded-xl border border-[#E9DDCA] bg-white"
             style={{ boxShadow: "0 5px 18px rgba(74,55,40,0.055)" }}
             data-testid="brand-reference-item">
      <button type="button" onClick={() => onOpen(reference.id)}
              className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C7A574]"
              aria-label="Открыть референс" data-testid="brand-reference-open">
        <div className="aspect-[4/3] bg-[#F5F0E8] overflow-hidden">
          <PrivateReferenceImage reference={reference} onReady={onImageReady} className="block h-full w-full object-contain" />
        </div>
        <div className="h-9 px-3 flex items-center text-[10.5px] text-[#806B52]">
          {reference.note ? <span className="inline-flex items-center gap-1.5"><FileText size={12} /> Есть описание</span>
            : <span className="text-[#A5947E]">Открыть</span>}
        </div>
      </button>
    </article>
  );
}

export default function MOBrandReferences() {
  const nav = useNavigate();
  const pickerRef = useRef(null);
  const [profile, setProfile] = useState(undefined);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detailNote, setDetailNote] = useState("");
  const [imageUrls, setImageUrls] = useState({});

  const selectedReference = references.find((item) => item.id === selectedId) || null;
  const selectedIndex = selectedReference ? references.findIndex((item) => item.id === selectedReference.id) : -1;
  const rememberImageUrl = useCallback((id, url) => {
    setImageUrls((current) => current[id] === url ? current : { ...current, [id]: url });
  }, []);

  const openReference = (id) => {
    const reference = references.find((item) => item.id === id);
    if (!reference) return;
    setSelectedId(id);
    setDetailNote(reference.note || "");
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const current = await getBrandProfileV2();
      setProfile(current || null);
      setReferences(current ? await listBrandReferences() : []);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    const available = MAX_BRAND_REFERENCES - references.length;
    if (available <= 0) return toast.error("В одной подборке можно сохранить до 20 референсов.");
    setBusy(true);
    const next = [...references];
    let failures = 0;
    for (const file of selected.slice(0, available)) {
      if (!ALLOWED_TYPES.has(file.type) || file.size > 15 * 1024 * 1024) { failures += 1; continue; }
      try {
        const media = await uploadUserMediaFile(file, { title: "Референс бренда" });
        const created = await createBrandReference({ media_id: media.id, note: "", order: next.length });
        next.push(created);
        setReferences([...next]);
      } catch { failures += 1; }
    }
    setBusy(false);
    if (failures) toast.error(`Не удалось добавить: ${failures}. Остальные изображения сохранены.`);
    if (selected.length > available) toast.info("В одной подборке можно сохранить до 20 референсов.");
  };

  const saveNote = async (id, note) => {
    setBusy(true);
    try {
      const updated = await updateBrandReference(id, { note: note.trim() });
      setReferences((items) => items.map((item) => item.id === id ? updated : item));
      setDetailNote(updated.note || "");
      toast.success("Описание сохранено");
    } catch (requestError) { toast.error(errorText(requestError)); }
    finally { setBusy(false); }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= references.length) return;
    const previous = [...references];
    const reordered = [...references];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setReferences(reordered);
    setBusy(true);
    try {
      await Promise.all([
        updateBrandReference(reordered[index].id, { order: index }),
        updateBrandReference(reordered[target].id, { order: target }),
      ]);
      setReferences(await listBrandReferences());
    } catch (requestError) {
      setReferences(previous);
      toast.error(errorText(requestError));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await deleteBrandReference(id);
      const remaining = references.filter((item) => item.id !== id);
      await Promise.all(remaining.map((item, order) =>
        item.order === order ? Promise.resolve() : updateBrandReference(item.id, { order })
      ));
      setReferences(await listBrandReferences());
      setSelectedId(null);
      toast.success("Референс удалён");
    } catch (requestError) { toast.error(errorText(requestError)); }
    finally { setBusy(false); }
  };

  return (
    <BrandStudioShell title="Референсы">
      <main className="w-full max-w-6xl min-w-0 overflow-x-clip pb-16" data-testid="mo-brand-references">
        <button type="button" onClick={() => nav("/brand-studio")}
                className="mb-7 min-h-11 px-3 rounded-xl inline-flex items-center gap-1.5 text-[12.5px] text-[#4A3728] bg-[#F7F1E7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50">
          <ArrowLeft size={14} /> Вернуться в Brand Studio
        </button>

        {loading ? (
          <div className="min-h-[260px] grid place-items-center text-[13px] text-[#806B52]" data-testid="brand-references-loading">
            <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Загружаем референсы…</span>
          </div>
        ) : error ? (
          <section className="py-10"><h1 className="text-3xl font-serif text-[#2A1D14]">Не удалось загрузить референсы</h1>
            <p className="mt-3 text-sm text-[#806B52]">{error}</p><Button onClick={load} className="mt-5">Повторить</Button></section>
        ) : !profile ? (
          <section className="max-w-xl py-10" data-testid="brand-references-no-profile">
            <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#8B6F4B]">МОЙ БРЕНД</div>
            <h1 className="mt-2 text-[36px] md:text-[44px] leading-tight font-serif text-[#2A1D14]">Сначала расскажи немного о своём бренде</h1>
            <Button onClick={() => nav("/brand-studio/brand-brief")} className="mt-6 min-h-11 bg-[#4A3728] text-[#F7EFE0]">Начать создание</Button>
          </section>
        ) : (
          <>
            <header className="max-w-3xl">
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#8B6F4B]">МОЙ БРЕНД</div>
              <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
                <div><h1 className="text-[38px] md:text-[50px] leading-none font-serif text-[#2A1D14]">Покажи, что тебе нравится</h1>
                  <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-[#5B4326]">Собери изображения, детали и настроение, которые помогут найти визуальное направление твоего бренда.</p></div>
                <span className="text-[12px] text-[#8B6F4B]" data-testid="brand-reference-count">
                  {referenceCountLabel(references.length)}
                </span>
              </div>
            </header>
            <input ref={pickerRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only"
                   onChange={upload} aria-label="Выбрать изображения для референсов" />

            {!references.length ? (
              <section className="mt-10 max-w-2xl py-8 border-y border-[#E9DDCA]" data-testid="brand-references-empty">
                <ImagePlus size={25} className="text-[#9B7B54]" />
                <h2 className="mt-5 text-[28px] md:text-[34px] leading-tight font-serif text-[#2A1D14]">Собери своё визуальное направление</h2>
                <p className="mt-3 text-[14px] leading-relaxed text-[#5B4326]">Добавь изображения, в которых есть что-то твоё. Это может быть упаковка, цвет, шрифт, фотография, иллюстрация или просто настроение.</p>
                <Button type="button" disabled={busy} onClick={() => pickerRef.current?.click()} className="mt-6 min-h-11 bg-[#4A3728] text-[#F7EFE0]">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Добавить референсы
                </Button>
                <p className="mt-4 text-[12px] leading-relaxed text-[#806B52]">Не нужно искать идеальный пример целиком — достаточно одной детали, которая тебе нравится.</p>
              </section>
            ) : (
              <>
                <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
                  {references.length >= MAX_BRAND_REFERENCES && <span className="text-[11.5px] text-[#806B52]">
                    В одной подборке можно сохранить до 20 референсов.
                  </span>}
                  <Button type="button" variant="outline" disabled={busy || references.length >= MAX_BRAND_REFERENCES}
                    onClick={() => pickerRef.current?.click()} className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728] hover:bg-[#F7F1E7]">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Добавить референсы
                  </Button>
                </div>
                <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
                         data-testid="brand-references-board">
                  {references.map((reference) => <ReferenceItem key={reference.id} reference={reference}
                    onOpen={openReference} onImageReady={rememberImageUrl} />)}
                </section>
                <section className="mt-12 max-w-3xl border-t border-[#E9DDCA] pt-8" data-testid="brand-references-next-step">
                  <div className="text-[10.5px] tracking-[0.2em] uppercase text-[#8B6F4B]">СЛЕДУЮЩИЙ ШАГ</div>
                  <h2 className="mt-2 text-[30px] md:text-[36px] leading-tight font-serif text-[#2A1D14]">Всё готово для следующего шага</h2>
                  <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-[#5B4326]">
                    Мы собрали твои идеи и визуальные ориентиры. Теперь можно определить направление, которое станет основой будущего бренда.
                  </p>
                  <Button type="button" onClick={() => nav("/brand-studio/visual-direction")}
                          className="mt-6 min-h-11 bg-[#4A3728] text-[#F7EFE0] hover:bg-[#5B432F]"
                          data-testid="brand-references-next-step-action">
                    Найти визуальное направление
                  </Button>
                </section>
              </>
            )}

            <Dialog open={Boolean(selectedReference)} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
              <DialogContent className="w-[calc(100%-1.5rem)] max-w-4xl max-h-[calc(100vh-1.5rem)] overflow-y-auto border-[#E7DAC5] bg-[#FFFDF8] p-0 rounded-xl">
                {selectedReference && <div className="grid min-w-0 md:grid-cols-[minmax(0,1.15fr)_minmax(280px,.85fr)]" data-testid="brand-reference-detail">
                  <div className="min-h-[240px] max-h-[72vh] grid place-items-center overflow-hidden bg-[#F5F0E8] p-3">
                    {imageUrls[selectedReference.id]
                      ? <img src={imageUrls[selectedReference.id]} alt="Визуальный референс бренда" className="block max-h-[68vh] max-w-full object-contain" />
                      : <div className="grid place-items-center gap-2 text-[12px] text-[#806B52]"><Loader2 size={18} className="animate-spin" /> Загружаем изображение…</div>}
                  </div>
                  <div className="min-w-0 p-5 pt-12 md:p-7 md:pt-12">
                    <DialogTitle className="text-[26px] leading-tight font-serif font-normal text-[#2A1D14]">Что тебе здесь нравится?</DialogTitle>
                    <DialogDescription className="mt-2 text-[12px] leading-relaxed text-[#806B52]">
                      Цвет, типографика, настроение, упаковка, иллюстрация, композиция или отдельная деталь.
                    </DialogDescription>
                    <Textarea value={detailNote} maxLength={1000} rows={5} onChange={(event) => setDetailNote(event.target.value)}
                              className="mt-4 min-h-[112px] max-h-[260px] resize-y overflow-y-auto border-[#E7DAC5] bg-white"
                              aria-label="Описание референса" data-testid="brand-reference-note" />
                    <Button type="button" disabled={busy || detailNote.trim() === (selectedReference.note || "")}
                            onClick={() => saveNote(selectedReference.id, detailNote)}
                            className="mt-4 min-h-11 bg-[#4A3728] text-[#F7EFE0] hover:bg-[#5B432F]">
                      Сохранить описание
                    </Button>
                    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#E9DDCA] pt-4">
                      <Button type="button" variant="outline" disabled={busy || selectedIndex === 0}
                              onClick={() => move(selectedIndex, -1)} aria-label="Переместить референс выше"
                              className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] px-3 text-[#5B4326] hover:bg-[#F7F1E7]">
                        <ArrowUp size={15} /> Выше
                      </Button>
                      <Button type="button" variant="outline" disabled={busy || selectedIndex === references.length - 1}
                              onClick={() => move(selectedIndex, 1)} aria-label="Переместить референс ниже"
                              className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] px-3 text-[#5B4326] hover:bg-[#F7F1E7]">
                        <ArrowDown size={15} /> Ниже
                      </Button>
                      <Button type="button" variant="ghost" disabled={busy} onClick={() => remove(selectedReference.id)}
                              aria-label="Удалить референс" className="min-h-11 px-3 text-[#8D463F] hover:bg-[#FBF1EF]">
                        <Trash2 size={15} /> Удалить
                      </Button>
                    </div>
                  </div>
                </div>}
              </DialogContent>
            </Dialog>
          </>
        )}
        <footer className="pt-10 pb-2">
          <BrandStudioSignature />
        </footer>
      </main>
    </BrandStudioShell>
  );
}
